'use client';

import { Suspense, useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { track, flush, startAutoFlush, stopAutoFlush } from '@/lib/tracker';

/** Interactive elements whose clicks/hovers we auto-capture. */
const INTERACTIVE_TAGS = new Set(['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA']);
const INTERACTIVE_SELECTOR = Array.from(INTERACTIVE_TAGS)
  .map((t) => t.toLowerCase())
  .join(',');

/** Max length for label text extracted from elements. */
const MAX_LABEL_LEN = 80;

function getClickLabel(el: HTMLElement): string {
  // Prefer explicit data-track attribute
  const dataTrack = el.closest('[data-track]')?.getAttribute('data-track');
  if (dataTrack) return dataTrack;

  // For buttons/links, use text content
  const text = el.textContent?.trim() ?? '';
  return text.length > MAX_LABEL_LEN ? text.slice(0, MAX_LABEL_LEN) : text;
}

function getClickTarget(el: HTMLElement): string {
  // Prefer data-track
  const tracked = el.closest('[data-track]');
  if (tracked) return `[data-track="${tracked.getAttribute('data-track')}"]`;

  // Build a minimal selector: tag + id or first class
  const tag = el.tagName.toLowerCase();
  if (el.id) return `${tag}#${el.id}`;
  const cls = el.className && typeof el.className === 'string' ? el.className.split(' ')[0] : '';
  return cls ? `${tag}.${cls}` : tag;
}

/**
 * Build full path including search params for page tracking.
 */
function buildFullPath(pathname: string, search: string): string {
  return search ? `${pathname}?${search}` : pathname;
}

/**
 * Inner component that uses useSearchParams (requires Suspense boundary).
 */
function AnalyticsInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prevFullPath = useRef<string | null>(null);

  // Track page views on route or query param change
  useEffect(() => {
    const search = searchParams.toString();
    const fullPath = buildFullPath(pathname, search);

    if (fullPath !== prevFullPath.current) {
      track({ type: 'page_viewed', page: fullPath });
      prevFullPath.current = fullPath;
    }
  }, [pathname, searchParams]);

  // Lifecycle: auto-flush, click/hover capture, visibility flush
  useEffect(() => {
    startAutoFlush();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush();
      }
    };

    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;

      const interactive = el.closest(INTERACTIVE_SELECTOR) as HTMLElement | null;
      if (!interactive) return;

      track({
        type: 'click',
        target: getClickTarget(interactive),
        label: getClickLabel(interactive),
      });
    };

    let lastHoverTarget: Element | null = null;

    const onMouseOver = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;

      const interactive = el.closest(INTERACTIVE_SELECTOR);
      if (!interactive || interactive === lastHoverTarget) return;
      lastHoverTarget = interactive;

      track({
        type: 'hover',
        target: getClickTarget(interactive as HTMLElement),
        label: getClickLabel(interactive as HTMLElement),
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('click', onClick, { capture: true });
    document.addEventListener('mouseover', onMouseOver, {
      capture: true,
      passive: true,
    } as AddEventListenerOptions);

    return () => {
      stopAutoFlush();
      flush();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('mouseover', onMouseOver, { capture: true } as EventListenerOptions);
    };
  }, []);

  return null;
}

/**
 * Invisible component that wires the clickstream tracker to:
 * - route changes (page_viewed events with full query params)
 * - global click capture on interactive elements
 * - global hover capture on interactive elements (deduplicated)
 * - visibilitychange flush
 * - periodic auto-flush
 *
 * Wrapped in Suspense because useSearchParams requires it for
 * static page generation (Next.js export mode).
 */
export function AnalyticsProvider() {
  return (
    <Suspense fallback={null}>
      <AnalyticsInner />
    </Suspense>
  );
}
